import {
  cityWays,
  Pick,
  type CityWay,
  type Point,
  type Spot,
  type WaySaid,
} from '@trippath/shared';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { ModeIcon } from '../icons/ModeIcon';
import { SpotField } from '../place/SpotField';

/** One part of a way as typed: by what, to where, its minutes as written. */
export interface PartDraft {
  by: CityWay;
  to: Spot | null;
  minutes: string;
}

/** A way as typed: by what and how long, or in parts when it has changes. */
export interface WayDraft {
  by: CityWay | null;
  minutes: string;
  parts: PartDraft[];
}

/** Minutes on the way as typed: whole, at most a day; none said, `null`; else `undefined`. */
export function minutesOf(typed: string): number | null | undefined {
  if (typed.trim() === '') return null;
  const minutes = /^\s*\d{1,4}\s*$/.test(typed) ? Number(typed) : 1441;
  return minutes > 1440 ? undefined : minutes;
}

/** A way said, as the fields type it. */
export function draftOf(said: WaySaid | null | undefined): WayDraft {
  return {
    by: said?.by ?? null,
    minutes: said?.byMinutes?.toString() ?? '',
    parts: (said?.parts ?? []).map((part) => ({
      by: part.by,
      to: part.to ? { ...part.to, detail: '' } : null,
      minutes: part.minutes?.toString() ?? '',
    })),
  };
}

/**
 * The way typed, as it is sent; or what is wrong with it: minutes not minutes, or a part but the
 * last without its end. In parts, it goes by its first part's way, and takes all their minutes
 * when each is said.
 */
export function wayOf(draft: WayDraft): WaySaid | 'wrongMinutes' | 'partEnd' {
  const minutes = minutesOf(draft.minutes);
  const each = draft.parts.map((part) => minutesOf(part.minutes));
  if (minutes === undefined || each.includes(undefined)) return 'wrongMinutes';
  const last = draft.parts.length - 1;
  if (draft.parts.some((part, at) => at < last && part.to === null)) return 'partEnd';
  const [first] = draft.parts;
  if (!first) return { by: draft.by, byMinutes: minutes, parts: [] };
  const said = each.flatMap((one) => (one === null || one === undefined ? [] : [one]));
  return {
    by: first.by,
    byMinutes: said.length === each.length ? said.reduce((all, one) => all + one, 0) : null,
    parts: draft.parts.map((part, at) => ({
      by: part.by,
      to: part.to ? { lat: part.to.lat, lon: part.to.lon, name: part.to.name } : null,
      minutes: each[at] ?? null,
    })),
  };
}

interface Props {
  way: WayDraft;
  onWay: (way: WayDraft) => void;
  /** Where the town is: a part's end is looked for near it. */
  near: Point | null;
  /** Where the way ends: the last part goes there. */
  end: string;
  error: string | undefined;
}

/**
 * How a place is got to from the one before — walked unless said — and the minutes it takes; with
 * changes, in parts: from the airport by train to the centre, by bus to a stop, on foot to the
 * hotel, each part's end a station or a stop found by its name or its GPS.
 */
export function WayFields({ way, onWay, near, end, error }: Props) {
  const { t } = useTranslation();
  const id = useId();
  const ways = cityWays.map((value) => ({
    value,
    label: t(`city.by.${value}`),
    icon: <ModeIcon mode={value} size={16} />,
  }));
  const minutesField = (key: string, value: string, set: (typed: string) => void) => (
    <div className="field">
      <label className="field-label" htmlFor={`${id}-${key}`}>
        {t('city.thing.byMinutes')}
      </label>
      <div className="input-group">
        <input
          id={`${id}-${key}`}
          className="text-input booking-time"
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={value}
          aria-invalid={error !== undefined}
          onChange={(event) => {
            set(event.target.value);
          }}
        />
        <span className="input-suffix" aria-hidden="true">
          min
        </span>
      </div>
    </div>
  );
  const part = (at: number, change: Partial<PartDraft>) => {
    onWay({
      ...way,
      parts: way.parts.map((one, i) => (i === at ? { ...one, ...change } : one)),
    });
  };
  // A change added: the way so far its first part, on foot after it unless said.
  const addChange = () => {
    const first: PartDraft[] =
      way.parts.length > 0
        ? way.parts
        : [{ by: way.by ?? 'walk', to: null, minutes: way.minutes }];
    onWay({ ...way, parts: [...first, { by: 'walk', to: null, minutes: '' }] });
  };
  // A part taken out: down to one, the way in one part again.
  const removePart = (at: number) => {
    const left = way.parts.filter((_, i) => i !== at);
    const [only] = left;
    onWay(
      left.length === 1 && only
        ? { by: only.by, minutes: only.minutes, parts: [] }
        : { ...way, parts: left },
    );
  };
  const last = way.parts.length - 1;
  return (
    <div className="way-fields">
      {way.parts.length === 0 ? (
        <div className="agenda-when agenda-way">
          <Pick
            label={t('city.thing.by')}
            options={ways}
            value={way.by}
            // Walked unless said: shown as walking is, with its mark.
            placeholder={
              <span className="pick-icon-label">
                <ModeIcon mode="walk" size={16} />
                {t('city.by.walk')}
              </span>
            }
            searchable={false}
            onChange={(by) => {
              onWay({ ...way, by });
            }}
            onClear={() => {
              onWay({ ...way, by: null });
            }}
          />
          {minutesField('minutes', way.minutes, (minutes) => {
            onWay({ ...way, minutes });
          })}
        </div>
      ) : (
        <ol className="way-parts" aria-label={t('city.thing.by')}>
          {way.parts.map((one, at) => (
            <li key={String(at)}>
              <div className="agenda-when agenda-way-part">
                <Pick
                  label={t('city.thing.part', { n: at + 1 })}
                  options={ways}
                  value={one.by}
                  searchable={false}
                  onChange={(by) => {
                    part(at, { by });
                  }}
                />
                {minutesField(String(at), one.minutes, (minutes) => {
                  part(at, { minutes });
                })}
                <button
                  type="button"
                  className="icon-button icon-button-small way-part-remove"
                  aria-label={t('city.thing.removePart', { n: at + 1 })}
                  title={t('city.thing.removePart', { n: at + 1 })}
                  onClick={() => {
                    removePart(at);
                  }}
                >
                  ×
                </button>
              </div>
              {at < last ? (
                <SpotField
                  label={t('city.thing.partTo')}
                  near={near}
                  spot={one.to}
                  onPick={(to) => {
                    part(at, { to });
                  }}
                  onClear={() => {
                    part(at, { to: null });
                  }}
                />
              ) : (
                <p className="quiet way-part-end">{t('city.thing.lastTo', { place: end })}</p>
              )}
            </li>
          ))}
        </ol>
      )}
      <button type="button" className="text-link way-add" onClick={addChange}>
        {t('city.thing.addChange')}
      </button>
      {error !== undefined && <p className="field-error">{error}</p>}
    </div>
  );
}
